import React from "react";
import GrupArgeVoltageGauge from "../../components/GrupArgeVoltageGauge";
import CurrentBar from "../../components/CurrentBar";
import CosfiMiniGauge from "../../components/CosfiMiniGauge";
import GrupArgeRatioGauge from "../../components/GrupArgeRatioGauge";
import GrupArgeWeeklyActiveEnergyChart from "../../components/GrupArgeWeeklyActiveEnergyChart";
import GrupArgeWeeklyReactiveEnergyChart from "../../components/GrupArgeWeeklyReactiveEnergyChart";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

// ----------------------------
// REACTIVE ENERGY PIE (Local Copy)
// ----------------------------
function ReactiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const realImport = importVal || 0;
    const realExport = exportVal || 0;

    const drawImport = realImport > 0 ? realImport : 0.001;
    const drawExport = realExport > 0 ? realExport : 0.001;

    const data = [
        { name: t('dashboard.inductiveEnergy'), value: drawImport },
        { name: t('dashboard.capacitiveEnergy'), value: drawExport },
    ];

    const COLORS = ["#3B82F6", "#EF4444"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {t('dashboard.reactiveEnergyDist')}
            </h3>

            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="60%"
                        outerRadius={110}
                        dataKey="value"
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLORS[i]} />
                        ))}
                    </Pie>

                    <Tooltip
                        formatter={(v, _, item) =>
                            `${Number(item?.payload?.value).toFixed(2)} kVArh`
                        }
                    />
                    <Legend
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                        wrapperStyle={{
                            transform: "translateY(15px)",
                        }}
                    />

                </PieChart>

            </div>
        </div>
    );
}

// ----------------------------
// ACTIVE ENERGY PIE (Local Copy)
// ----------------------------
function ActiveEnergyPie({ importVal = 0, exportVal = 0 }) {
    const { t } = useTranslation();
    const realImport = importVal || 0;
    const realExport = exportVal || 0;

    const drawImport = realImport > 0 ? realImport : 0.001;
    const drawExport = realExport > 0 ? realExport : 0.001;

    const data = [
        { name: t('dashboard.activeConsumption'), value: drawImport }, // Tüketim
        { name: t('dashboard.activeProduction'), value: drawExport },   // Üretim
    ];

    const COLORS = ["#10B981", "#F59E0B"];

    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {t('dashboard.activeEnergyDist')}
            </h3>

            <div className="flex items-center justify-center h-72">
                <PieChart width={300} height={300}>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="60%"
                        outerRadius={110}
                        dataKey="value"
                    >
                        {data.map((_, i) => (
                            <Cell key={i} fill={COLORS[i]} />
                        ))}
                    </Pie>

                    <Tooltip
                        formatter={(v, _, item) =>
                            `${Number(item?.payload?.value).toFixed(2)} kWh`
                        }
                    />
                    <Legend
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                        wrapperStyle={{
                            transform: "translateY(15px)",
                        }}
                    /></PieChart>

            </div>
        </div>
    );
}

export default function GrupArgeDashboard({ data, deviceModel, deviceId, refreshKey }) {
    const { t } = useTranslation();
    if (!data) {
        return (
            <div className="p-6 text-center text-slate-500">
                {t('hierarchy.loading')}
            </div>
        );
    }

    // Grup Arge API'den dönen veriler:
    // voltages: { VL1, VL2, VL3 }
    // currents: { I1, I2, I3 }
    // cosfi: { CosFi1, CosFi2, CosFi3, CosFiT: null } (CosFiT null geliyordu, o yüzden L1-L2-L3 ortalamasını gösterebiliriz veya sadece 3 fazı)
    // activeEnergy: { PT_IMPORT, PT_EXPORT }
    // reactiveEnergy: { QT_IMPORT, QT_EXPORT } (Backend'de bu isimlerle dönüyor mu? getGrupArgeRealtimeLite kontrol edilmeli. 
    // Eğer dönmüyorsa pie chartlar boş kalır, sorun değil.)

    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    return (
        <div className="space-y-6">

            {/* VOLTAJ + AKIM */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t('dashboard.phaseVoltages')}
                </h3>

                <div className="flex flex-col xl:flex-row gap-4 items-center overflow-x-auto pb-4">
                    {/* Voltajlar */}
                    <div className="flex flex-row gap-4 flex-shrink-0">
                        {["VL1", "VL2", "VL3"].map((key) => (
                            <div key={key} className="flex-shrink-0">
                                <GrupArgeVoltageGauge
                                    label={key}
                                    value={data.voltages?.[key]}
                                    vtr={1}
                                    isLine={false}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Akımlar - Voltajların hemen devamına */}
                    <div className="flex flex-row items-end justify-center gap-6 flex-shrink-0 px-4">
                        <CurrentBar label="I1" value={data.currents?.I1} max={100} />
                        <CurrentBar label="I2" value={data.currents?.I2} max={100} />
                        <CurrentBar label="I3" value={data.currents?.I3} max={100} />
                    </div>
                </div>
            </div>

            {/* COSFI */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <CosfiMiniGauge label="Cosφ L1" value={data.cosfi?.CosFi1} />
                <CosfiMiniGauge label="Cosφ L2" value={data.cosfi?.CosFi2} />
                <CosfiMiniGauge label="Cosφ L3" value={data.cosfi?.CosFi3} />
            </div>

            {/* RATIO GAUGES */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <GrupArgeRatioGauge
                    deviceId={deviceId}
                    type="end"
                    title={t('dashboard.totalInductive')}
                />

                <GrupArgeRatioGauge
                    deviceId={deviceId}
                    type="cap"
                    title={t('dashboard.totalCapacitive')}
                />
            </div>

            {/* PASTA GRAFİKLERİ (ÖZET ENERJİ) */}
            {/* Not: Realtime endpointi enerji toplamlarını dönüyorsa burası çalışır. 
                 Eğer dönmüyorsa 0 görünür. Kullanıcı dashboard istiyor, ileride backend güncellenirse otomatik dolar. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ActiveEnergyPie
                    importVal={data.activeEnergy?.PT_IMPORT ?? 0}
                    exportVal={data.activeEnergy?.PT_EXPORT ?? 0}
                />

                <ReactiveEnergyPie
                    importVal={data.reactiveEnergy?.QT_IMPORT ?? 0}
                    exportVal={data.reactiveEnergy?.QT_EXPORT ?? 0}
                />
            </div>

            {/* HAFTALIK GRAFİKLER */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <GrupArgeWeeklyActiveEnergyChart
                    deviceId={deviceId}
                    API_BASE={API_BASE}
                    refreshKey={refreshKey}
                />

                <GrupArgeWeeklyReactiveEnergyChart
                    deviceId={deviceId}
                    API_BASE={API_BASE}
                    refreshKey={refreshKey}
                />
            </div>

        </div>
    );
}
